import React from "react"
import { FormAutocomplete, FormTextField } from "@carrotfertility/carotene-core-x"
import { CountryMap } from "content/CountryMap"
import { AboutYou } from "types/carrotPlanTypes"
import { useIntl } from "react-intl"
import { Stack, useTheme } from "@carrotfertility/carotene-core"

export type InternationalAddressFields = Omit<AboutYou["address"], "address1" | "address2" | "internationalCountry"> & {
  internationalCountry: { label: string; value: string }
}

export function isValidInternationalAddress(address: Omit<AboutYou["address"], "address1" | "address2">) {
  return (
    Boolean(address?.internationalAddressLine) &&
    Boolean(address?.internationalCountry) &&
    Boolean(address?.city) &&
    Boolean(address?.state)
  )
}

const CountryOptions = Object.keys(CountryMap).map((key) => ({
  label: CountryMap[key],
  value: key
}))

export function formatInternationalAddressFieldsToPayload(fields: InternationalAddressFields) {
  return {
    address: {
      city: fields?.city,
      state: fields?.state,
      zip: fields?.zip,
      internationalCountry: fields?.internationalCountry.value,
      internationalAddressLine: fields?.internationalAddressLine
    }
  }
}

interface AboutYouFormInternationalAddressProps {
  defaultValues?: Partial<Omit<AboutYou["address"], "address1" | "address2">>
}

export function InternationalAddress(props: AboutYouFormInternationalAddressProps): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const requiredText = intl.formatMessage({ defaultMessage: "Required" })
  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <FormTextField
        minRows={4}
        multiline
        defaultValue={props?.defaultValues?.internationalAddressLine}
        registerOptions={{
          required: requiredText
        }}
        inputProps={{ "data-testid": "about-you-internationalAddressLine" }}
        placeholder={intl.formatMessage({ defaultMessage: "Enter house number and street name" })}
        label={intl.formatMessage({ defaultMessage: "Street Address" })}
        name="internationalAddressLine"
      />
      <FormTextField
        defaultValue={props?.defaultValues?.city}
        registerOptions={{
          required: requiredText
        }}
        inputProps={{ "data-testid": "about-you-city" }}
        placeholder={intl.formatMessage({ defaultMessage: "Enter city" })}
        label={intl.formatMessage({ defaultMessage: "City" })}
        name="city"
      />
      <FormTextField
        defaultValue={props?.defaultValues?.state}
        registerOptions={{
          required: requiredText
        }}
        inputProps={{ "data-testid": "about-you-state" }}
        placeholder={intl.formatMessage({ defaultMessage: "Enter state, province, country, or region" })}
        label={intl.formatMessage({ defaultMessage: "State / Province / County / Region" })}
        name="state"
      />
      <FormAutocomplete
        fullWidth
        defaultValue={props?.defaultValues?.internationalCountry}
        controllerProps={{
          defaultValue: CountryOptions.find(({ value }) => value === props?.defaultValues?.internationalCountry),
          rules: { required: requiredText }
        }}
        data-testid={"about-you-internationalCountry"}
        options={CountryOptions}
        placeholder={intl.formatMessage({ defaultMessage: "Select country" })}
        label={intl.formatMessage({ defaultMessage: "Country" })}
        name="internationalCountry"
      />
      <FormTextField
        defaultValue={props?.defaultValues?.zip}
        placeholder={intl.formatMessage({ defaultMessage: "Enter postal code" })}
        inputProps={{ "data-testid": "about-you-zip" }}
        label={intl.formatMessage({ defaultMessage: "Postal code" })}
        name="zip"
        helperText={intl.formatMessage({ defaultMessage: "Postal code is only required if your address has one." })}
      />
    </Stack>
  )
}
